<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import InboxTeamsRoutingTable from '../InboxTeamsRoutingTable.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();

const selectedTeamIds = ref([]);
const derivedAgentNames = ref([]);
const isTeamListUpdating = ref(false);

// { [teamId]: { routing_type_id, agent_bot_id } }
const teamConfigs = reactive({});
const defaultTeamId = ref(null);

const teamList = computed(() => store.getters['teams/getTeams']);
const routingTypes = computed(
  () => store.getters['routingTypes/getRoutingTypes']
);
const agentBots = computed(() => store.getters['agentBots/getBots']);

const selectedTeamNames = computed(() =>
  selectedTeamIds.value.map(
    id => teamList.value.find(team => team.id === id)?.name ?? ''
  )
);

const teamMenuItems = computed(() =>
  teamList.value
    .filter(({ id }) => !selectedTeamIds.value.includes(id))
    .map(({ id, name }) => ({
      label: name,
      value: id,
      action: 'select',
    }))
);

const selectedTeams = computed(() =>
  selectedTeamIds.value
    .map(id => teamList.value.find(team => team.id === id))
    .filter(Boolean)
);

const ensureTeamConfig = teamId => {
  if (!teamConfigs[teamId]) {
    teamConfigs[teamId] = { routing_type_id: '', agent_bot_id: '' };
  }
};

const handleTeamAdd = ({ value }) => {
  if (!selectedTeamIds.value.includes(value)) {
    selectedTeamIds.value.push(value);
    ensureTeamConfig(value);
  }
};

const handleTeamRemove = index => {
  const [removedId] = selectedTeamIds.value.splice(index, 1);
  delete teamConfigs[removedId];
  if (defaultTeamId.value === removedId) {
    defaultTeamId.value = null;
  }
};

const handleConfigUpdate = ({ teamId, field, value }) => {
  ensureTeamConfig(teamId);
  teamConfigs[teamId][field] = value;
};

const fetchAttachedTeams = async () => {
  try {
    const response = await store.dispatch('inboxTeams/get', {
      inboxId: props.inbox.id,
    });
    const {
      data: {
        payload: { teams, agents },
      },
    } = response;

    selectedTeamIds.value = teams.map(team => team.id);
    derivedAgentNames.value = agents.map(agent => agent.name);

    // reset & repopulate configs from server state
    Object.keys(teamConfigs).forEach(key => delete teamConfigs[key]);
    defaultTeamId.value = null;

    teams.forEach(team => {
      const link = team.inbox_team || {};
      teamConfigs[team.id] = {
        routing_type_id: link.routing_type_id || '',
        agent_bot_id: link.agent_bot_id || '',
      };
      if (link.is_default) {
        defaultTeamId.value = team.id;
      }
    });
  } catch (error) {
    // Handle error silently
  }
};

const updateTeams = async () => {
  if (selectedTeamIds.value.length && !defaultTeamId.value) {
    useAlert(t('INBOX_MGMT.TEAMS.ROUTING.DEFAULT_TEAM_REQUIRED'));
    return;
  }

  isTeamListUpdating.value = true;
  try {
    const teamConfigsPayload = selectedTeamIds.value.map(teamId => ({
      team_id: teamId,
      is_default: teamId === defaultTeamId.value,
      routing_type_id: teamConfigs[teamId]?.routing_type_id || null,
      agent_bot_id: teamConfigs[teamId]?.agent_bot_id || null,
    }));

    await store.dispatch('inboxTeams/update', {
      inboxId: props.inbox.id,
      teamConfigs: teamConfigsPayload,
    });
    await fetchAttachedTeams();
    useAlert(t('INBOX_MGMT.TEAMS.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.TEAMS.UPDATE_ERROR'));
  }
  isTeamListUpdating.value = false;
};

watch(() => props.inbox.id, fetchAttachedTeams);

onMounted(async () => {
  await Promise.all([
    store.dispatch('teams/get'),
    store.dispatch('routingTypes/get'),
    store.dispatch('agentBots/get'),
  ]);
  fetchAttachedTeams();
});
</script>

<template>
  <div>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.TEAMS.TITLE')"
      :help-text="$t('INBOX_MGMT.TEAMS.SUB_TEXT')"
      class="[&>div]:!items-start"
    >
      <div
        class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
      >
        <TagInput
          :model-value="selectedTeamNames"
          :placeholder="$t('INBOX_MGMT.TEAMS.PICK_TEAMS')"
          :menu-items="teamMenuItems"
          show-dropdown
          skip-label-dedup
          :auto-open-dropdown="false"
          @add="handleTeamAdd"
          @remove="handleTeamRemove"
        />
      </div>

      <template #extra>
        <div v-if="selectedTeams.length" class="mt-4">
          <InboxTeamsRoutingTable
            v-model:default-team-id="defaultTeamId"
            :selected-teams="selectedTeams"
            :team-configs="teamConfigs"
            :routing-types="routingTypes"
            :agent-bots="agentBots"
            @update-config="handleConfigUpdate"
          />
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-8">
          <div class="col-span-1 lg:col-span-2" />
          <div class="col-span-1 lg:col-span-6 mt-4 justify-self-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isTeamListUpdating"
              @click="updateTeams"
            />
          </div>
        </div>
      </template>
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="derivedAgentNames.length"
      :label="$t('INBOX_MGMT.TEAMS.DERIVED_AGENTS')"
      :help-text="$t('INBOX_MGMT.TEAMS.DERIVED_AGENTS_SUB_TEXT')"
      class="mt-6 [&>div]:!items-start"
    >
      <div class="flex flex-wrap gap-2">
        <span
          v-for="agentName in derivedAgentNames"
          :key="agentName"
          class="px-2 py-1 text-sm rounded-lg bg-n-slate-3 text-n-slate-12"
        >
          {{ agentName }}
        </span>
      </div>
    </SettingsFieldSection>
  </div>
</template>
